/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

/**
 * The only place that talks to the API.
 *
 * Same origin, so the session cookie rides along on its own — no
 * `credentials:'include'`, no CORS preflight, no `SameSite=None`. That is the
 * whole point of splitting by path rather than by hostname (plan §4).
 *
 * Two rules this file exists to enforce:
 *
 *  1. **A 401 is not an error to display, it is a session that ended.** It gets
 *     its own type so the router can send the person to /login rather than
 *     showing them "Not authenticated" inside a broken board.
 *  2. **Identity is never sent from here.** The server reads `req.user` from the
 *     verified cookie. Trusting the client once already misattributed every note
 *     on the production board, and those rows cannot be corrected because the
 *     real author was never recorded. No request body in this app carries an
 *     author, a creator, or an email that names the person acting.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** The session ended or never existed. Route, do not render. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * Out of scope. The API answers 404 rather than 403 for a task in a circle you
   * are not in, deliberately — a 403 would confirm the task exists. Treat this as
   * "not there", never as "you are not allowed", or the UI leaks what the API
   * carefully does not.
   */
  get isMissing(): boolean {
    return this.status === 404;
  }
}

type Json = Record<string, unknown> | unknown[];

/**
 * One refresh at a time, shared by everything waiting on it.
 *
 * The board fires several requests together, so an expired token produces a
 * burst of 401s at once. Without this, each would post its own `/auth/refresh`
 * and all but one would be racing to rotate a cookie the others are still using.
 */
let refreshInFlight: Promise<boolean> | null = null;

function trySilentRefresh(): Promise<boolean> {
  refreshInFlight ??= fetch('/auth/refresh', { method: 'POST' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init?.headers,
    },
  });

  /**
   * A 401 is a token that expired, until a refresh says otherwise.
   *
   * The session cookie outlives the token deliberately, so an ordinary working
   * day crosses at least one expiry. **What is at stake is unsaved typing**: the
   * person mid-way through a task description gets the save retried and it
   * succeeds, instead of being thrown to /login with the form gone. The vanilla
   * app has done this since it was written (`app.js:79`); the first React draft
   * had no refresh at all, which was a regression rather than an omission.
   *
   * Retried once, never twice — a second 401 after a successful refresh means
   * something other than expiry, and looping on it would hide it.
   *
   * Refresh failing is not an error to show. It means the session really has
   * ended, and the ApiError carries `isUnauthenticated` for whoever is watching.
   *
   * **For a query, `<RequireSession>` sees that and routes to /login. For a
   * mutation, nothing does** — a failed write never makes `/auth/me` re-run, so
   * the session query sits on its last good answer. `queryClient.ts` closes
   * that: a 401 from a mutation asks the session query again, and *that* is what
   * produces the redirect.
   *
   * Deliberately not `window.location = '/auth/sso/login'` the way the vanilla
   * app does: a hard navigation here would discard the router's record of where
   * the person was trying to go.
   */
  if (res.status === 401 && !isRetry && path !== '/auth/refresh') {
    if (await trySilentRefresh()) return request<T>(path, init, true);
  }

  if (res.status === 204) return undefined as T;

  // The API answers JSON for everything under /api. Anything else means a proxy
  // or nginx answered instead of Express — worth saying plainly rather than
  // failing later inside a JSON.parse nobody can read.
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      if (!res.ok) throw new ApiError(res.status, `${res.status} from ${path} (not JSON)`, text);
      throw new ApiError(res.status, `Expected JSON from ${path}, got ${text.slice(0, 60)}`, text);
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : `${res.status} from ${path}`);
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: Json) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: Json) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: Json) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  /**
   * A body is optional and almost always absent — the one caller that needs it
   * is unsubscribing a push device, where *which* device is the whole request
   * and cannot go in the path: an endpoint is a URL, complete with its own query
   * string, and encoding one into another URL is how a `%2F` ends up being
   * decoded by a proxy nobody controls.
   */
  delete: <T>(path: string, body?: Json) =>
    request<T>(path, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) }),
  /** Attachments. FormData sets its own Content-Type boundary, so we must not. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};
